import React from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";

const DetailItem = ({ icon, label, value }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={1}
    alignItems={{ xs: "flex-start", sm: "center" }}
    sx={{ py: 0.2 }}
  >
    <Avatar sx={{ width: 22, height: 22 }}>
      {React.cloneElement(icon, {
        sx: { fontSize: 14, color: "primary.main" },
      })}
    </Avatar>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", lineHeight: 1 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ overflowWrap: "anywhere" }}>
        {value ?? ""}
      </Typography>
    </Box>
  </Stack>
);

export default function ClientRoleDetails({
  role,
  mode = "view",
  editForm,
  fieldSx,
  updating = false,
  loading = false,
  onEditFormChange,
  onUpdateRole,
}) {
  const isEditing = mode === "edit";

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: "rgba(255,255,255,0.05)",
          color: "white",
        }}
      >
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={26} sx={{ color: "white" }} />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        background: "rgba(255,255,255,0.05)",
        color: "white",
      }}
    >
      <Stack alignItems="center" spacing={0.8} mb={1.5}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "primary.main",
            fontSize: 20,
          }}
        >
          {role?.roleName?.charAt(0) || <BadgeOutlinedIcon />}
        </Avatar>

        <Box textAlign="center">
          <Typography variant="subtitle2" fontWeight={600}>
            {role?.roleName || "Client Role"}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {isEditing ? (
        <Stack component="form" spacing={2} onSubmit={onUpdateRole}>
          <TextField
            fullWidth
            label="Role Name"
            value={editForm?.roleName ?? ""}
            onChange={(event) =>
              onEditFormChange?.("roleName", event.target.value)
            }
            placeholder="Enter role name"
            sx={fieldSx}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={updating || !String(editForm?.roleName ?? "").trim()}
            sx={{
              width: { xs: "100%", sm: 160 },
              maxWidth: 240,
              alignSelf: "center",
              textTransform: "none",
              borderRadius: 2,
              background: "#4f8a3d",
              "&:hover": {
                background: "#3f6f31",
              },
            }}
          >
            {updating ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Update Client Role"
            )}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <DetailItem
            icon={<TagOutlinedIcon />}
            label="Role Name"
            value={role?.roleName}
          />
        </Stack>
      )}
    </Paper>
  );
}
